import { useController, useFormContext } from "react-hook-form";
import {
  useFieldProps,
  FieldBaseProps,
} from "@shared/features/form/useFieldProps";
import { SelectableChip } from "./SelectableChip";

export type Props = FieldBaseProps & {
  name: string;
  options: {
    label: string;
    value: string;
  }[];
  maxItems?: number;
  disabled?: boolean;
  id?: string;
};

export function SelectChips({
  name,
  options,
  maxItems = Infinity,
  required = false,
  disabled = false,
  error,
  isLoading,
  isSubmitting,
  className,
  id,
}: Props) {
  const isMultiSelect = maxItems > 1;
  const form = useFormContext();

  const {
    field: { value = [], onChange, onBlur },
  } = useController({
    name,
    control: form.control,
    defaultValue: [],
    rules: {
      required: required ? "This field is required" : false,
    },
  });

  const selectedValues: string[] = Array.isArray(value) ? value : [];

  const handleChange = (optionValue: string, checked: boolean) => {
    let newValue: string[];

    if (checked) {
      if (isMultiSelect) {
        if (selectedValues.length >= maxItems) return;
        newValue = [...selectedValues, optionValue];
      } else {
        newValue = [optionValue];
      }
    } else {
      newValue = selectedValues.filter((val) => val !== optionValue);
    }

    onChange(newValue);
  };

  const fieldProps = useFieldProps({
    className,
    error,
    isLoading,
    isSubmitting,
    required,
  });

  return (
    <div
      {...fieldProps}
      className="border-none"
      aria-labelledby={`${id}-label`}
      role="group"
    >
      <div
        className="flex flex-row flex-wrap gap-x-2 gap-y-3 items-center justify-start"
        onBlur={onBlur}
      >
        {options.map((option) => {
          const { label, value } = option;

          const isChecked = selectedValues.includes(value);
          const isDisabled =
            disabled || (!isChecked && selectedValues.length >= maxItems);

          return (
            <SelectableChip
              key={value}
              {...option}
              isChecked={isChecked}
              isDisabled={isDisabled}
              onChange={(checked) => handleChange(value, checked)}
              isSelectable
              isRemovable
              isCheckOnClick={!isMultiSelect}
              title={label}
            />
          );
        })}
      </div>
    </div>
  );
}

export default SelectChips;
